import React from 'react';
import { ArrowRight, Send, Archive, FolderOpen, Clock } from 'lucide-react';
import { ModuleActivity, formatAktivitaet } from '../../services/moduleActivity';
import { ACCENTS, AccentKey } from './moduleAccents';
import type { HubModulKennung } from '../../services/appUmfang';

/**
 * Die Kachel-Bausteine des Projekt-Hubs — aus ProjectModuleGrid.tsx
 * herausgezogen, damit ein zweites Raster (SifaModuleGrid.tsx, Projekttyp
 * „SiFa") dieselben Kacheln zeichnet, ohne den Bestand abzuschreiben. Beide
 * Raster importieren von hier; Verhalten und Aussehen sind unveraendert.
 *
 * Nur Komponenten und Typen: die Farbtabelle ACCENTS und der Leerwert
 * KEINE_AKTIVITAET stehen in moduleAccents.ts, weil die Lint-Regel
 * react-refresh/only-export-components (Ratsche) hier keine Konstanten duldet.
 */

export type ModuleStats = { used: number, contracted: number } | null;

/**
 * „Zuletzt 07.09.2026" unter jeder Kachel: wann in diesem Modul zuletzt
 * etwas geschehen ist (Quelle: useModuleActivity). Ohne Wert steht
 * ausdruecklich „Noch keine Aktivität" — eine leere Stelle liesse offen,
 * ob nichts geschah oder ob der Wert nur noch nicht geladen ist.
 */
export const ActivityLine: React.FC<{ value?: string | null; label?: string; className?: string }> = ({ value, label = 'Zuletzt', className = '' }) => {
    const text = formatAktivitaet(value);
    return (
        <p className={`text-[10px] font-medium relative z-10 flex items-center gap-1 ${text ? 'text-slate-500' : 'text-slate-300'} ${className}`}>
            <Clock className="w-3 h-3 shrink-0" />
            <span>{text ? `${label} ${text}` : 'Noch keine Aktivität'}</span>
        </p>
    );
};


export const SectionHeading: React.FC<{ label: string, hint?: string }> = ({ label, hint }) => (
    <div data-glass="abschnitt" className="flex items-center gap-3 mb-2.5 px-1">
        <h2 className="text-[11px] font-black uppercase tracking-[0.18em] text-slate-400 whitespace-nowrap">{label}</h2>
        <div className="h-px flex-1 bg-gradient-to-r from-slate-200 to-transparent" />
        {hint && <span className="text-[11px] text-slate-400 hidden md:inline">{hint}</span>}
    </div>
);

/**
 * Platzhalter fuer angekuendigte Module — gleiche Groesse wie eine Kachel,
 * bewusst ausgegraut und ohne Klickziel. Zurueckgeholt aus Stand 0858912
 * (dort „SoonTile"), weil das SiFa-Raster wieder Kacheln zeigt, hinter denen
 * noch kein Werkzeug steht (Gefaehrdungsbeurteilung, Betriebsanweisungen,
 * Faelligkeiten). Das SiGeKo-Raster braucht sie weiterhin nicht.
 */
export const AngekuendigtTile: React.FC<{ icon: React.ReactNode, title: string, subtitle: string }> = ({ icon, title, subtitle }) => (
    <div className="bg-slate-50/70 p-5 pb-6 rounded-2xl border border-dashed border-slate-300 relative overflow-hidden flex flex-col opacity-70">
        <div className="absolute top-3 right-3 bg-white text-slate-400 text-[10px] font-black px-2 py-0.5 rounded-md border border-slate-200 uppercase tracking-wider">
            Angekündigt
        </div>
        <div className="bg-white w-10 h-10 rounded-xl flex items-center justify-center mb-3 border border-slate-200">{icon}</div>
        <span className="text-[10px] font-black tracking-[0.2em] text-slate-300 block mb-0.5">BALD</span>
        <h3 className="font-bold text-slate-600 text-sm relative z-10">{title}</h3>
        <p className="text-[11px] text-slate-400 mt-0.5 mb-4 relative z-10 flex-1">{subtitle}</p>
        <div className="flex items-center text-slate-400 text-[11px] font-bold relative z-10 cursor-not-allowed">In Entwicklung</div>
    </div>
);

export interface TileProps {
    accent: AccentKey;
    /** Schritt im Projektverlauf ("01"). Die Ablage traegt bewusst keinen. */
    step?: string;
    /**
     * Ersetzt die Schrittnummer. Die Begehung ist kein einmaliger Schritt,
     * sondern laeuft ueber die ganze Bauzeit — eine Ziffer in der Kette waere
     * dort schlicht falsch.
     */
    badge?: string;
    /**
     * Farbe des Badges: 'accent' (Vorgabe) in der Kachelfarbe, 'alarm' rot —
     * die Fristen-Kachel meldet damit „3 ÜBERFÄLLIG", ohne dass die ganze
     * Kachel den Fehlerton bekommt.
     */
    badgeTone?: 'accent' | 'alarm';
    icon: React.ReactNode;
    title: string;
    subtitle: string;
    /**
     * Statuszeile direkt unter dem Titel — die Planungskachel zeigt hier
     * Ampel und Klaerungsquote (Entscheid E12), damit der Stand VOR dem
     * Untertitel steht. Fehlt sie, sieht die Kachel aus wie bisher.
     */
    status?: React.ReactNode;
    /**
     * Hinweiszeile zwischen Untertitel und Aktivitaet — etwa „Planungsleistung
     * beauftragt — Planungsphase noch nicht angelegt" (Hub-Hinweis 1.6).
     */
    notice?: React.ReactNode;
    cta: string;
    onClick: () => void;
    stats?: ModuleStats;
    /**
     * Ersetzt den Zaehler des Kontingents: die Begehungskachel zaehlt die
     * durchgefuehrten Berichte, nicht die gebuchten Positionen.
     */
    usedOverride?: number;
    /**
     * Ohne vergleichbaren Nenner faellt auch der Balken weg: er zeigte
     * dieselbe Quote noch einmal (25 von 2444 Stunden = 1 %) und liess ein
     * fast fertiges Projekt wie ein kaum begonnenes aussehen.
     */
    countOnly?: boolean;
    /** Tooltip auf dem Zaehler bei `countOnly`; Vorgabe ist der Begehungstext. */
    countOnlyHint?: string;
    hint?: string;
    className?: string;
    /** Letzte Aktivitaet im Modul (ISO oder YYYY-MM-DD); null = noch nichts. */
    lastActivity?: string | null;
    /** Wort vor dem Datum, Vorgabe „Zuletzt"; die Begehung sagt „Letzte Begehung". */
    activityLabel?: string;
    /** Ankername der Lernzentrum-Tour — wird unveraendert als `data-tutorial` gesetzt. */
    'data-tutorial'?: string;
}

const COUNT_ONLY_HINT = 'Durchgeführte Begehungen. Die Begehungen dieses Projekts werden nach Stunden abgerechnet — ein Begehungs-Kontingent gibt es nicht.';

export const ModuleTile: React.FC<TileProps> = ({ accent, step, badge, badgeTone = 'accent', icon, title, subtitle, status, notice, cta, onClick, stats, usedOverride, countOnly = false, countOnlyHint = COUNT_ONLY_HINT, hint, className = '', lastActivity, activityLabel, 'data-tutorial': tutorialAnker }) => {
    const a = ACCENTS[accent];
    const hasQuota = !!stats && stats.contracted > 0;
    const used = usedOverride !== undefined ? usedOverride : (stats?.used ?? 0);
    const percent = hasQuota ? Math.min(100, Math.round((used / stats.contracted) * 100)) : 0;
    const isComplete = hasQuota && used >= stats.contracted;

    return (
        <div
            data-tutorial={tutorialAnker}
            onClick={onClick}
            title={hint}
            className={`group bg-white p-5 pb-6 rounded-2xl border border-slate-200 ${a.border} hover:shadow-lg hover:-translate-y-1 transition-all duration-300 cursor-pointer relative overflow-hidden flex flex-col ${className}`}
        >
            <div className={`absolute bottom-0 right-0 p-8 ${a.blob} rounded-full -mr-6 -mb-6 transition-transform group-hover:scale-150 duration-500 pointer-events-none`} />

            {hasQuota && (
                <div className="absolute top-3.5 right-3.5 z-20">
                    <span
                        title={countOnly ? countOnlyHint : undefined}
                        className={`text-[11px] font-black px-2 py-0.5 rounded-full shadow-sm border ${isComplete && !countOnly ? 'bg-emerald-100 text-emerald-700 border-emerald-200' : 'bg-white text-slate-600 border-slate-200'}`}
                    >
                        {countOnly ? used : `${used} / ${stats.contracted}`}
                    </span>
                </div>
            )}

            <div className={`${a.iconWrap} w-10 h-10 rounded-xl flex items-center justify-center mb-3 border group-hover:rotate-6 group-hover:scale-105 transition-transform relative z-10`}>
                {icon}
            </div>

            {badge
                ? <span className={`text-[10px] font-black tracking-[0.16em] ${badgeTone === 'alarm' ? 'text-red-600' : a.cta} relative z-10 block mb-0.5`}>{badge}</span>
                : step && <span className={`text-[10px] font-black tracking-[0.2em] ${a.step} relative z-10 block mb-0.5`}>{step}</span>}
            <h3 className="font-bold text-slate-800 text-sm relative z-10">{title}</h3>
            {status && <div className="mt-1 relative z-10 min-w-0">{status}</div>}
            <p className="text-[11px] text-slate-500 mt-0.5 mb-2 relative z-10 flex-1 leading-snug">{subtitle}</p>
            {notice && <div className="relative z-10 mb-2 min-w-0">{notice}</div>}
            <ActivityLine value={lastActivity} label={activityLabel} className="mb-3" />
            <div className={`flex items-center ${a.cta} text-[11px] font-bold relative z-10 group-hover:translate-x-2 transition-transform`}>
                {cta} <ArrowRight className="w-3 h-3 ml-1" />
            </div>

            {/* Die Kachel selbst ist der Fortschrittsbalken: eine Haarlinie an
                der Unterkante statt eines zweiten Miniaturbalkens unter dem
                Zaehler. */}
            {hasQuota && !countOnly && (
                <div className="absolute inset-x-0 bottom-0 h-1 bg-slate-100">
                    <div className={`h-full ${isComplete ? 'bg-emerald-400' : a.bar} transition-all duration-700`} style={{ width: `${percent}%` }} />
                </div>
            )}
        </div>
    );
};

/** Die drei Eingaenge der Ablage-Karte — dieselben Modulschluessel wie im Bestand. */
export type AblageTarget = 'dokumente' | 'module-archive' | 'module-sent-reports';

interface AblageProps {
    onNavigate: (module: AblageTarget) => void;
    activity: ModuleActivity;
    /** Das Archiv bleibt Schreibtischarbeit und fehlt auf dem Handy. */
    isMobile: boolean;
    /**
     * Handy-App (docs/android-app-plan.md 1.4): welche Eingaenge gezeigt
     * werden. Fehlt die Angabe, ist alles sichtbar wie bisher (Browser,
     * Tablet-App). Bleibt kein Eingang uebrig, entfaellt die ganze Karte.
     */
    modulSichtbar?: (kennung: HubModulKennung) => boolean;
}

/**
 * ABLAGE — eine Karte, drei Eingaenge.
 * Archiv und Sendeverlauf sind keine eigenen Arbeitsschritte,
 * sondern zwei Sichten auf dieselbe Frage („was liegt zu diesem
 * Projekt vor, und was davon ist rausgegangen?"). Sie stehen
 * deshalb hier und nicht mehr als gleichwertige Kacheln zwischen
 * den Modulen des Projektverlaufs.
 * Das Archiv bleibt Schreibtischarbeit (`!isMobile`); Ablage und
 * Sendeverlauf werden auch auf der Baustelle gebraucht.
 *
 * Gemeinsam fuer beide Raster (SiGeKo/HSSE und SiFa): die Ablage haengt an
 * Tabellen (`project_documents`, `report_history`, `email_logs`), nicht am
 * Projekttyp.
 */
export const AblageKarte: React.FC<AblageProps> = ({ onNavigate, activity, isMobile, modulSichtbar }) => {
    // Ohne `modulSichtbar` ist jeder Eingang sichtbar — Browser und Tablet-App wie bisher.
    const zeige = (kennung: AblageTarget) => !modulSichtbar || modulSichtbar(kennung);
    const dokumente = zeige('dokumente');
    const archiv = !isMobile && zeige('module-archive');
    const sendeverlauf = zeige('module-sent-reports');
    if (!dokumente && !archiv && !sendeverlauf) return null;
    return (
    <section>
        <SectionHeading label="Ablage" hint="Alles zum Projekt an einer Stelle" />
        <div className="bg-white rounded-2xl border border-slate-200 hover:border-indigo-300 hover:shadow-lg transition-all duration-300 relative overflow-hidden group/hub">
            <div className="absolute top-0 right-0 p-14 bg-indigo-50/60 rounded-full -mr-14 -mt-14 transition-transform group-hover/hub:scale-125 duration-500 pointer-events-none" />
            <div className="flex flex-col lg:flex-row lg:items-stretch relative z-10">

                {dokumente && (
                <button
                    onClick={() => onNavigate('dokumente')}
                    className="flex-1 text-left p-5 flex items-center gap-3.5 group/main cursor-pointer"
                >
                    <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 border border-indigo-100 w-12 h-12 rounded-xl flex items-center justify-center shrink-0 group-hover/main:rotate-6 group-hover/main:scale-105 transition-transform">
                        <FolderOpen className="w-6 h-6 text-indigo-600" />
                    </div>
                    <div className="min-w-0">
                        <h3 className="font-bold text-slate-800">Dokumente</h3>
                        <p className="text-[11px] text-slate-500 mt-0.5">Pläne, Verträge, Nachweise — mit Archiv und Sendeverlauf</p>
                        <ActivityLine value={activity.dokumente} className="mt-1" />
                        <div className="flex items-center text-indigo-600 text-[11px] font-bold mt-1.5 group-hover/main:translate-x-2 transition-transform">
                            Ablage öffnen <ArrowRight className="w-3 h-3 ml-1" />
                        </div>
                    </div>
                </button>
                )}

                {(archiv || sendeverlauf) && (
                <div className={dokumente
                    ? `border-t lg:border-t-0 lg:border-l border-slate-100 grid grid-cols-1 shrink-0 ${isMobile ? 'lg:w-[200px]' : 'sm:grid-cols-2 lg:w-[360px]'}`
                    : `flex-1 grid grid-cols-1 ${isMobile ? '' : 'sm:grid-cols-2'}`}>
                    {archiv && (
                        <button
                            onClick={() => onNavigate('module-archive')}
                            className="text-left p-4 sm:p-5 hover:bg-slate-50 transition-colors group/sub border-b sm:border-b-0 sm:border-r border-slate-100"
                        >
                            <div className="flex items-center gap-2.5 mb-1">
                                <div className="bg-slate-100 w-8 h-8 rounded-lg flex items-center justify-center border border-slate-200 group-hover/sub:scale-105 transition-transform">
                                    <Archive className="w-4 h-4 text-slate-600" />
                                </div>
                                <span className="font-bold text-slate-800 text-sm">Archiv</span>
                            </div>
                            <p className="text-[11px] text-slate-500 leading-snug">Alle Dokumente &amp; Protokolle</p>
                            <ActivityLine value={activity.archiv} label="Zuletzt archiviert" className="mt-1" />
                            <div className="flex items-center text-slate-600 text-[11px] font-bold mt-1.5 group-hover/sub:translate-x-1 transition-transform">
                                Ansehen <ArrowRight className="w-3 h-3 ml-1" />
                            </div>
                        </button>
                    )}

                    {sendeverlauf && (
                    <button
                        onClick={() => onNavigate('module-sent-reports')}
                        className="text-left p-4 sm:p-5 hover:bg-blue-50/40 transition-colors group/sub"
                    >
                        <div className="flex items-center gap-2.5 mb-1">
                            <div className="bg-blue-50 w-8 h-8 rounded-lg flex items-center justify-center border border-blue-100 group-hover/sub:scale-105 transition-transform">
                                <Send className="w-4 h-4 text-blue-600" />
                            </div>
                            <span className="font-bold text-slate-800 text-sm">Sendeverlauf</span>
                        </div>
                        <p className="text-[11px] text-slate-500 leading-snug">Status des E-Mail-Versands</p>
                        <ActivityLine value={activity.sendeverlauf} label="Zuletzt gesendet" className="mt-1" />
                        <div className="flex items-center text-blue-600 text-[11px] font-bold mt-1.5 group-hover/sub:translate-x-1 transition-transform">
                            Postfach öffnen <ArrowRight className="w-3 h-3 ml-1" />
                        </div>
                    </button>
                    )}
                </div>
                )}
            </div>
        </div>
    </section>
    );
};
